import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "holiday-remote" }
const _hoisted_2 = { class: "year-selector" }
const _hoisted_3 = { class: "holidays-grid" }
const _hoisted_4 = { class: "month-header" }
const _hoisted_5 = { class: "month-title" }
const _hoisted_6 = { class: "holidays-list" }
const _hoisted_7 = ["data-holiday-id"]
const _hoisted_8 = { class: "holiday-inputs" }
const _hoisted_9 = {
  key: 0,
  class: "no-holidays"
}

import { ref, computed, nextTick } from 'vue';
import axios from 'axios';

// Props
interface Props {
  showButton?: boolean;
}

interface Holiday {
  hh_idx?: number;
  hh_date: string;
  hh_holiday_name: string;
  day?: number;
  isNew?: boolean;
  isDeleted?: boolean;
  tempId?: string;
  isValid?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'HolidayRemote',
  props: {
    showButton: { type: Boolean, default: false }
  },
  setup(__props: any, { expose: __expose }) {

const props = __props;

const dialog = ref(false);
const selectedYear = ref(new Date().getFullYear());
const holidays = ref<Holiday[]>([]);
const saving = ref(false);
const snackbar = ref({ 
  show: false, 
  message: '', 
  color: 'success',
  icon: 'mdi-check-circle'
});

const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
});

// 날짜를 YYYY-MM-DD 형식으로 변환하는 함수
const formatDateToYYYYMMDD = (dateString: string): string => {
  if (!dateString) return '';
  
  // 이미 YYYY-MM-DD 형식인 경우 그대로 반환
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }
  
  // ISO 8601 형식 또는 다른 날짜 형식인 경우 Date 객체로 변환
  const date = new Date(dateString);
  
  // 유효하지 않은 날짜인 경우 빈 문자열 반환
  if (isNaN(date.getTime())) {
    return '';
  }
  
  // YYYY-MM-DD 형식으로 변환
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

const loadHolidays = async () => {
  try {
    const response = await axios.get(`/api/calc/holidays/${selectedYear.value}`);
    if (response.data.success) {
      holidays.value = response.data.data.map((h: Holiday) => ({
        ...h,
        day: new Date(h.hh_date).getDate(),
        isNew: false,
        isDeleted: false,
        isValid: true
      }));
    }
  } catch (error) {
    console.error('공휴일 조회 실패:', error);
    showSnackbar('공휴일 조회에 실패했습니다', 'error', 'mdi-alert-circle');
  }
};

const getHolidaysByMonth = (month: number) => {
  return holidays.value.filter(h => {
    if (h.isDeleted) return false;
    const date = new Date(h.hh_date);
    return date.getMonth() + 1 === month && date.getFullYear() === selectedYear.value;
  });
};

const getDaysInMonth = (month: number) => {
  return new Date(selectedYear.value, month, 0).getDate();
};

const addHoliday = (month: number) => {
  const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const newHoliday: Holiday = {
    hh_date: `${selectedYear.value}-${String(month).padStart(2, '0')}-01`,
    hh_holiday_name: '',
    day: 1,
    isNew: true,
    isDeleted: false,
    tempId,
    isValid: false
  };
  
  holidays.value.push(newHoliday);
  
  // 새로 추가된 항목에 포커스
  nextTick(() => {
    const nameInput = document.querySelector(`[data-holiday-id="${tempId}"] .name-input input`) as HTMLInputElement;
    if (nameInput) {
      nameInput.focus();
    }
  });
  
  showSnackbar(`${month}월에 공휴일을 추가했습니다`, 'info', 'mdi-plus-circle');
};

const updateHolidayDate = (holiday: Holiday, month: number) => {
  // day가 빈 값이거나 null/undefined인 경우 처리하지 않음
  if (holiday.day === null || holiday.day === undefined || holiday.day === '') {
    // 백스페이스로 지운 경우 이전 값으로 복원하지 않음 (사용자가 입력 중일 수 있음)
    return;
  }
  
  const day = Number(holiday.day);
  const maxDay = getDaysInMonth(month);
  
  // 유효하지 않은 숫자인 경우 처리하지 않음
  if (isNaN(day) || day < 1) {
    return;
  }
  
  // 일자 유효성 검사
  if (day > maxDay) {
    holiday.day = maxDay;
    showSnackbar(`${month}월은 ${maxDay}일까지만 있습니다`, 'warning', 'mdi-alert');
  } else {
    holiday.day = day;
  }
  
  // 유효한 day 값이 있을 때만 날짜 업데이트
  if (holiday.day && holiday.day >= 1 && holiday.day <= maxDay) {
    holiday.hh_date = `${selectedYear.value}-${String(month).padStart(2, '0')}-${String(holiday.day).padStart(2, '0')}`;
  }
  
  validateHolidayName(holiday);
};

// 포커스를 잃었을 때 빈 값이면 기본값으로 복원
const handleDayBlur = (holiday: Holiday, month: number) => {
  if (holiday.day === null || holiday.day === undefined || holiday.day === '') {
    // 이전에 유효한 날짜가 있었으면 그것으로 복원, 없으면 1로 설정
    if (holiday.hh_date) {
      const date = new Date(holiday.hh_date);
      holiday.day = date.getDate();
    } else {
      holiday.day = 1;
    }
    updateHolidayDate(holiday, month);
  }
};

const validateHolidayName = (holiday: Holiday) => {
  const name = holiday.hh_holiday_name?.trim();
  holiday.isValid = Boolean(name && name.length > 0);
  
  if (name && name.length > 50) {
    holiday.hh_holiday_name = name.substring(0, 50);
    showSnackbar('공휴일명은 50자 이내로 입력해주세요', 'warning', 'mdi-alert');
  }
};

const removeHoliday = (holiday: Holiday) => {
  if (holiday.isNew) {
    const index = holidays.value.indexOf(holiday);
    holidays.value.splice(index, 1);
    showSnackbar('공휴일을 삭제했습니다', 'info', 'mdi-delete');
  } else {
    holiday.isDeleted = true;
    showSnackbar('공휴일을 삭제 목록에 추가했습니다', 'warning', 'mdi-delete-outline');
  }
};

const saveHolidays = async () => {
  // 유효성 검사
  const invalidHolidays = holidays.value.filter(h => 
    !h.isDeleted && (!h.hh_holiday_name?.trim() || !h.day)
  );
  
  if (invalidHolidays.length > 0) {
    showSnackbar('공휴일명과 일자를 모두 입력해주세요', 'error', 'mdi-alert-circle');
    return;
  }

  saving.value = true;
  try {
    let count = 0;
    const errors: string[] = [];

    // 삭제
    const deletes = holidays.value.filter(h => h.isDeleted && h.hh_idx);
    for (const h of deletes) {
      try {
        await axios.delete(`/api/calc/holidays/${h.hh_idx}`);
        count++;
      } catch (error) {
        errors.push(`삭제 실패: ${h.hh_holiday_name}`);
      }
    }

    // 추가
    const inserts = holidays.value.filter(h => h.isNew && !h.isDeleted && h.hh_holiday_name.trim());
    for (const h of inserts) {
      try {
        const formattedDate = formatDateToYYYYMMDD(h.hh_date);
        if (!formattedDate) {
          errors.push(`날짜 형식 오류: ${h.hh_holiday_name}`);
          continue;
        }
        await axios.post('/api/calc/holidays', {
          hh_date: formattedDate,
          hh_holiday_name: h.hh_holiday_name
        });
        count++;
      } catch (error) {
        errors.push(`추가 실패: ${h.hh_holiday_name}`);
      }
    }

    // 수정
    const updates = holidays.value.filter(h => !h.isNew && !h.isDeleted && h.hh_idx);
    for (const h of updates) {
      try {
        const formattedDate = formatDateToYYYYMMDD(h.hh_date);
        if (!formattedDate) {
          errors.push(`날짜 형식 오류: ${h.hh_holiday_name}`);
          continue;
        }
        await axios.put(`/api/calc/holidays/${h.hh_idx}`, {
          hh_date: formattedDate,
          hh_holiday_name: h.hh_holiday_name
        });
        count++;
      } catch (error) {
        errors.push(`수정 실패: ${h.hh_holiday_name}`);
      }
    }

    if (errors.length > 0) {
      showSnackbar(`일부 저장 실패: ${errors.length}건`, 'warning', 'mdi-alert');
    } else {
      showSnackbar(`저장 완료! (${count}건 처리)`, 'success', 'mdi-check-circle');
    }
    
    await loadHolidays();
  } catch (error) {
    console.error('저장 실패:', error);
    showSnackbar('저장 중 오류가 발생했습니다', 'error', 'mdi-alert-circle');
  } finally {
    saving.value = false;
  }
};

const showSnackbar = (message: string, color: string = 'success', icon: string = 'mdi-check-circle') => {
  snackbar.value = { show: true, message, color, icon };
};

// 다이얼로그 열릴 때 데이터 로드
const openDialog = () => {
  dialog.value = true;
  loadHolidays();
};

// 외부에서 접근 가능하도록 노출
__expose({
  openDialog
});

return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_snackbar = _resolveComponent("v-snackbar")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (__props.showButton)
      ? (_openBlock(), _createBlock(_component_v_btn, {
          key: 0,
          color: "primary",
          variant: "elevated",
          onClick: openDialog,
          "prepend-icon": "mdi-calendar"
        }, {
          default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
            _createTextVNode(" 공휴일 관리 ", -1)
          ]))]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createVNode(_component_v_dialog, {
      modelValue: dialog.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((dialog).value = $event)),
      "max-width": "800px"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_title, { class: "dialog-title" }, {
              default: _withCtx(() => [
                _cache[8] || (_cache[8] = _createTextVNode(" 공휴일 관리 ", -1)),
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  icon: "",
                  variant: "text",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (dialog.value = false))
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, null, {
                      default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                        _createTextVNode("mdi-close", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_text, { class: "dialog-content" }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_2, [
                  _createVNode(_component_v_select, {
                    modelValue: selectedYear.value,
                    "onUpdate:modelValue": [
                      _cache[1] || (_cache[1] = ($event: any) => ((selectedYear).value = $event)),
                      loadHolidays
                    ],
                    items: yearOptions.value,
                    label: "연도 선택",
                    variant: "outlined",
                    density: "compact",
                    "hide-details": "",
                    class: "year-select"
                  }, null, 8, ["modelValue", "items"])
                ]),
                _createElementVNode("div", _hoisted_3, [
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(12, (month) => {
                    return _createElementVNode("div", {
                      key: month,
                      class: "month-card"
                    }, [
                      _createElementVNode("div", _hoisted_4, [
                        _createElementVNode("span", _hoisted_5, _toDisplayString(month) + "월", 1),
                        _createVNode(_component_v_btn, {
                          color: "primary",
                          variant: "outlined",
                          onClick: ($event: any) => (addHoliday(month)),
                          "prepend-icon": "mdi-plus"
                        }, {
                          default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                            _createTextVNode(" 추가 ", -1)
                          ]))]),
                          _: 1
                        }, 8, ["onClick"])
                      ]),
                      _createElementVNode("div", _hoisted_6, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getHolidaysByMonth(month), (holiday) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: holiday.hh_idx || holiday.tempId,
                            "data-holiday-id": holiday.tempId,
                            class: "holiday-item"
                          }, [
                            _createElementVNode("div", _hoisted_8, [
                              _createVNode(_component_v_text_field, {
                                modelValue: holiday.day,
                                "onUpdate:modelValue": [($event: any) => ((holiday.day) = $event), ($event: any) => (updateHolidayDate(holiday, month))],
                                type: "number",
                                min: "1",
                                max: getDaysInMonth(month),
                                variant: "outlined",
                                density: "compact",
                                "hide-details": "",
                                class: "day-input",
                                onBlur: ($event: any) => (handleDayBlur(holiday, month))
                              }, null, 8, ["modelValue", "onUpdate:modelValue", "max", "onBlur"]),
                              _cache[10] || (_cache[10] = _createElementVNode("span", { class: "day-label" }, "일", -1)),
                              _createVNode(_component_v_text_field, {
                                modelValue: holiday.hh_holiday_name,
                                "onUpdate:modelValue": ($event: any) => ((holiday.hh_holiday_name) = $event),
                                variant: "outlined",
                                density: "compact",
                                "hide-details": "",
                                placeholder: "공휴일명",
                                class: "name-input",
                                onInput: ($event: any) => (validateHolidayName(holiday))
                              }, null, 8, ["modelValue", "onUpdate:modelValue", "onInput"])
                            ]),
                            _createVNode(_component_v_btn, {
                              color: "error",
                              variant: "outlined",
                              onClick: ($event: any) => (removeHoliday(holiday)),
                              "prepend-icon": "mdi-delete"
                            }, {
                              default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                                _createTextVNode(" 삭제 ", -1)
                              ]))]),
                              _: 1
                            }, 8, ["onClick"])
                          ], 8, _hoisted_7))
                        }), 128)),
                        (getHolidaysByMonth(month).length === 0)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                              _createVNode(_component_v_icon, { color: "grey" }, {
                                default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                                  _createTextVNode("mdi-calendar-blank", -1)
                                ]))]),
                                _: 1
                              }),
                              _cache[13] || (_cache[13] = _createElementVNode("span", null, "공휴일 없음", -1))
                            ]))
                          : _createCommentVNode("", true)
                      ])
                    ])
                  }), 64))
                ])
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_actions, { class: "dialog-actions" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  variant: "text",
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (dialog.value = false))
                }, {
                  default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                    _createTextVNode("취소", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_component_v_btn, {
                  color: "primary",
                  variant: "elevated",
                  onClick: saveHolidays,
                  loading: saving.value
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, { class: "mr-1" }, {
                      default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                        _createTextVNode("mdi-content-save", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[16] || (_cache[16] = _createTextVNode(" 저장 ", -1))
                  ]),
                  _: 1
                }, 8, ["loading"])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_v_snackbar, {
      modelValue: snackbar.value.show,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((snackbar.value.show) = $event)),
      color: snackbar.value.color,
      timeout: 3000
    }, {
      actions: _withCtx(() => [
        _createVNode(_component_v_btn, {
          variant: "text",
          onClick: _cache[4] || (_cache[4] = ($event: any) => (snackbar.value.show = false))
        }, {
          default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
            _createTextVNode("닫기", -1)
          ]))]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(snackbar.value.message) + " ", 1)
      ]),
      _: 1
    }, 8, ["modelValue", "color"])
  ]))
}
}

})